import type { FunctionComponent } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import styled from 'styled-components'
import { login } from '../../utils/auth/auth'
import { toAddresses, toCadastralObjects, toEstablishments } from '../../../links'
import { StyledList, StyledListItem } from './NoSearchResults'
import { formatNoResultsMessage } from './utils'
import Paragraph from '../Paragraph/Paragraph'
import Heading from '../Heading/Heading'
import Link, { Variant } from '../Links/Link/Link'

export interface NoDataSearchResultsProps {
  query: string
  unauthorized: string[]
}

const StyledParagraph = styled(Paragraph)`
  margin-top: 0;
`

const StyledLink = styled(Link)``

const NoDataSearchResults: FunctionComponent<NoDataSearchResultsProps> = ({
  query,
  unauthorized,
  ...otherProps
}) => {
  const handleLogin = () => {
    login()
  }

  return (
    <div {...otherProps}>
      <StyledParagraph small>{formatNoResultsMessage(query, 'Data')}</StyledParagraph>
      <Heading type="h2">Algemene zoeksuggesties</Heading>
      <StyledParagraph small>Maak de zoekcriteria eventueel minder specifiek. </StyledParagraph>
      <StyledParagraph small>Zoeken binnen ‘Data’ kan op de volgende onderdelen:</StyledParagraph>
      <Heading type="h3">Adressen, straten en andere openbare ruimten</Heading>
      <StyledList>
        <StyledListItem>
          Naam of postcode (eventueel met huisnummer, letter en toevoeging).
        </StyledListItem>
        <StyledListItem>
          BAG-identificatie (landelijke 16-cijferig nummer van openbare ruimte, nummeraanduiding,
          verblijfsobject, standplaats, of ligplaats).
        </StyledListItem>
        <StyledListItem>
          Blader (en filter) door de{' '}
          <StyledLink variant={Variant.Inline} forwardedAs={RouterLink} to={toAddresses()}>
            adressentabel
          </StyledLink>
          .
        </StyledListItem>
      </StyledList>
      <Heading type="h3">Panden</Heading>
      <StyledParagraph small>Zoek panden op pandnaam en bag-identificatie.</StyledParagraph>
      <Heading type="h3">Kadastrale objecten</Heading>
      <StyledList>
        <StyledListItem>
          Zoek kadastrale objecten op (delen van) kadastrale aanduiding.
        </StyledListItem>
        <StyledListItem>
          Blader (en filter) door de{' '}
          <StyledLink variant={Variant.Inline} forwardedAs={RouterLink} to={toCadastralObjects()}>
            kadastertabel
          </StyledLink>
          .
        </StyledListItem>
      </StyledList>
      <Heading type="h3">Gebieden</Heading>
      <StyledParagraph small>Zoek gebieden op naam en bouwblokken op bouwblokcode.</StyledParagraph>
      <Heading type="h3">Meetbouten</Heading>
      <StyledParagraph small>Zoek gebieden op meetboutnummer of bouwblokcode.</StyledParagraph>
      <Heading type="h3">Monumenten</Heading>
      <StyledParagraph small>Zoek monumenten op naam.</StyledParagraph>
      <Heading type="h3">bouw- en omgevingsdossiers</Heading>
      <StyledParagraph small>
        Zoek bouw- en omgevingsdossiers op stadsdeelcode met dossiernummer of op OLO of liaan nummer
        met voorvoegsel OLO (bijv: &lsquo;OLO123121&lsquo;).
      </StyledParagraph>
      <Heading type="h2">Ingelogde medewerkers</Heading>
      {!!unauthorized && (
        <StyledParagraph small>
          Medewerkers/ketenpartners van Gemeente Amsterdam kunnen{' '}
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <StyledLink variant={Variant.Inline} onClick={handleLogin}>
            inloggen
          </StyledLink>{' '}
          om meer gegevens te zien.
        </StyledParagraph>
      )}
      <Heading type="h3">Vestigingen</Heading>
      <StyledList>
        <StyledListItem>
          Zoek vestigingen (alleen voor ingelogde gebruikers) op handelsnaam of vestigingsnummer
        </StyledListItem>
        <StyledListItem>
          Blader (en filter) door de{' '}
          <StyledLink variant={Variant.Inline} forwardedAs={RouterLink} to={toEstablishments()}>
            handelsregistertabel
          </StyledLink>
          .
        </StyledListItem>
      </StyledList>
      <Heading type="h3">Maatschappelijke activiteiten</Heading>
      <StyledParagraph small>
        Zoek maatschappelijke activiteiten (alleen voor ingelogde gebruikers) op handelsnaam of
        KvK-nummer.
      </StyledParagraph>
      <Heading type="h3">Kadastrale subjecten</Heading>
      <StyledParagraph small>
        Zoek kadastrale subjecten (natuurlijke en niet-natuurlijke personen, alleen voor ingelogde
        gebruikers) op naam.
      </StyledParagraph>
    </div>
  )
}

export default NoDataSearchResults
